// HeaderTabs — segmented canvas-nav that replaces the centered HeaderSearch
// (2026-06-09 per Nika). Desktop/tablet layout:
//
//   [home] [search]  Studio · Files · Tasks · Agents  [arrow-down]
//
// • The home icon routes to the dashboard ('/') — an icon (2026-06-19 per Nika),
//   replacing the prior "Dashboard" text tab.
// • Text tabs switch the canvas view (route nav — the shell swaps the page;
//   the keep-alive cross-fade/background morph is layered on in a later pass).
// • The active text tab wears a lighter frosted rounded pill.
// • The search icon morphs `HeaderSearch` in (sets root._headerSearchOpen;
//   AppHeader swaps the pill for the search and the search's Escape clears it).
// • The arrow-down opens the apps launcher and, pressed again, closes it
//   (`toggleSearchAppsDropdown`).
//
// Mounted centered+absolute by AppHeader, mirroring the HeaderSearch pill it
// supersedes, so the left/right glass islands stay put.
//
// @mobileL (≤768px, the "one deck" merge — see AppHeader.js): this whole
// center zone collapses to ONE scrollable LABEL strip (MobileTabs/MobileTab,
// below) — Home folds in as its leading entry, Search relocates to the
// trailing zone (HeaderRight, AppHeader.js), Arrow hides outright (Search's
// focused input already opens the same apps dropdown). See MobileTabs' own
// comment for the full reasoning.

// Canvas tabs. `path` is the route the tab switches to.
export const HEADER_TABS = [
  { key: 'studio', label: 'shell.app.studio', path: '/studio' },
  { key: 'files', label: 'shell.app.files', path: '/files' },
  // Tickets→Tasks rename (2026-08-06 per Nika) — key stays 'tickets'
  // (internal identity); path + label are the canonical user-facing form.
  { key: 'tickets', label: 'shell.app.tasks', path: '/tasks' },
  { key: 'team', label: 'shell.app.team', path: '/team-and-agents' }
  // Home → '/' is the leading home icon (HomeBtn). Tickets/Team also live in the
  // left sidebar view-nav. "Team" → /team-and-agents (2026-06-19 per Nika).
]

// True when a route path belongs to a tab. '/' matches exactly (every path
// starts with '/'); the rest match the segment or a deeper sub-route.
import { AppScrollRow } from './AppScrollRow.js'
import { touchRow } from './_touchTarget.js'
import { stripWorkspacePrefix, canonicalRoutePath } from '../functions/workspacePrefix.js'
import { navItemVisible, featureEnabled } from '../functions/featureFlags.js'
import { workspaceSettings } from '../functions/workspaceSettings.js'
import { isCommHost } from '../functions/commHost.js'
import { commNavTabs } from '../functions/commProfileData.js'
import {
  applyNavbarTabPrefs,
  navbarTabPrefs,
  toggleCustomNavbarPin
} from '../functions/navbarTabMenu.js'

// THE TWO TAB STRIPS ARE AppScrollRows (components/AppScrollRow.js — the
// kit's one edge-fade row, shared with AppSegmented and the composer's chips):
// a strip wider than its room scrolls sideways with no scrollbar and FADES
// only the edge that hides a tab, so a tab the strip cuts fades into the edge
// ("fade away with shadow whats not fitting", Nika 2026-08-07) instead of
// reading as a cropped glyph; a cut CURRENT tab is brought to the middle.
//
// The band is `C` (2.618em, ~36px at the strip's 13.9px). The fade used to be
// `V` on both edges at all times — 0.09em, 1.25px measured live — which is a
// hard crop, not a fade: with the assistant drawer docked (1440px, a 15-tab
// workspace) the strip was 307px wide and the fourth tab showed 21px of 121,
// one glyph at full ink — "Contacts" read as "(" (DESIGN-WL-W2-SHELL-OVERLAYS-1,
// 2026-09-26). An edge with nothing beyond it paints no fade at all, so the
// first tab of a strip at rest keeps its full ink.
//
// The strips' one addition: never scroll during reorder mode or a pin drag
// over the strip — both read tab positions under the pointer.
const STRIP_ROW = {
  ...AppScrollRow,
  scope: {
    ...AppScrollRow.scope,
    edgeFadeCanReveal(el) {
      const root = el && el.state && el.state.root
      return !(root && (root._navbarReorder || root._navbarDragOverPath))
    }
  }
}

// FA204 / frank trap — every module-scope helper this file used to carry
// (ACTIVE_TAB_BG, _matchesPath, _navFromActiveWorkspaceRecord, _openTab,
// _visibleNavTabs, the long-press cluster, the drag/drop cluster,
// _openAppsLauncher, _navMatchPath, _applyTabSqueeze, _mobileTabActive, …)
// silently reads as undefined once a component is frank.toJSON'd for
// platform delivery (workspace d6ea1142,
// project_frank_drops_module_local_helpers — the live console proof this
// ticket exists for: `__scope._navMatchPath is not a function`). Every one
// now lives in `scope: {}` on the exported component that consumes it,
// called via `el.scope.<name>(...)`. A helper needed by MULTIPLE top-level
// components here (HeaderTabs/HeaderTab/MobileTab/SearchBtn) is DUPLICATED
// into each one's own scope, verbatim — object-literal scope properties
// can't reference each other across different top-level components, only
// within the SAME scope object via `this`. `HEADER_TABS` (above) stays a
// real module-level export (external cross-file importers:
// navSearchResultMenu.js, featureFlags.js, navbarTabMenu.js,
// workspaceUiManifest.js, two test files) — its OWN in-file consumer
// (`visibleNavTabs`, in HeaderTabs' scope) carries a literal copy instead of
// reading the export bare, since a bare in-file read of a sibling export is
// exactly as unresolvable post-frank.toJSON as a module-local const (see the
// readerVoice.js precedent — workspace 8732aa07).

// Top-level export (not just a HeaderTabs-nested key) so a SECOND mount can
// bare-key auto-extend it from HeaderRight (AppHeader.js) — the @mobileL
// trailing-zone position (2026-08-02, "give MobileTabs the center it was
// designed to own": search competing with the strip for HeaderTabs' width
// was the direct cause of a measured 80px strip window out of 212px
// available). Same content for both mounts; each independently controls its
// OWN visibility per breakpoint at its call site.
export const SearchBtn = {
  // The house tooltip, fed by `title` (TitleTooltipTrigger keeps the native one off).
  extends: 'NavbarRailControl',
  scope: {
    // Shared open paths for SearchBtn/ArrowBtn — each is wired to BOTH
    // onMousedown (pointer path: fires on the original target, immune to
    // the re-render node swaps that eat click events) and onClick (keyboard
    // path). ArrowBtn's two are idempotent (same-value state writes dedupe,
    // and openDropdown replaces its same-id layer); SearchBtn's toggle acts
    // once per press instead (its onMousedown note, below).
    //
    // A11Y-1 (2026-08-15): used to focus the input itself via
    // `setTimeout(80) + document.querySelector('[data-key="HeaderSearch"]
    // input')` — DOMQL never stamps a `data-key` attribute on the rendered
    // node, so the query always returned null, silently. `activeElement`
    // stayed BODY after every open, which also meant a real Escape press
    // could never reach HeaderSearch's Input.onKeydown handler (it's bound
    // to that specific element, not delegated) — one root cause explained
    // both "never focuses" AND "Escape doesn't collapse the morph" without
    // that Escape handler itself being broken. Fixed at the actual reveal
    // site instead: AppHeader.js's HeaderSearch already owns
    // `subscribeTo: ['_headerSearchOpen']` + onCreate/onUpdate, so it can
    // focus its OWN Input via the real DOMQL child reference
    // (`el.Form.Input.node`) once it's actually laid out — same
    // edge-detected-open/bounded-poll/return-focus idiom ContextDropdown.js's
    // `focusMenuOnOpen` already ships (a plain rAF/setTimeout-on-open is the
    // one that's fragile: the morph is still `opacity:0` / mid-transition at
    // that instant, and Chrome throttles rAF in backgrounded/automated tabs).
    //
    // This trigger's own job is just to remember WHERE to return focus on
    // dismiss — SearchBtn specifically, not "whatever was focused before"
    // (this handler calls `e.preventDefault()`, so the mousedown path never
    // gives this button default browser focus to fall back on).
    openHeaderSearchMorph(e, el, s) {
      e.preventDefault()
      e.stopPropagation()
      if (el?.node) s.root.__headerSearchReturnFocusTo = el.node
      s.root.update({ _headerSearchOpen: true })
      this.focusHeaderSearchInput(el)
    },
    // THE BUTTON TOGGLES (N2, Nika 2026-10-27: "fix navbar search button
    // click … for both left and top navbar modes"). On a rail the open pill
    // sits at the top of the screen and this button stays in the column
    // beside it, so its second press was a dead press: the open path above
    // is open-only. A press while the search is open now closes it, and the
    // morph's close edge (AppHeader focusSearchOnOpen) hands focus back to
    // this button. On a top bar the open pill covers the strip and this
    // button with it: the X, Escape and an outside press close it there.
    toggleHeaderSearchMorph(e, el, s) {
      if (!s?.root?._headerSearchOpen) return this.openHeaderSearchMorph(e, el, s)
      e.preventDefault()
      e.stopPropagation()
      el.call('closeHeaderSearch', { returnFocusTo: el?.node || null })
    },
    // iOS Safari raises the keyboard only for a focus made INSIDE the tap's
    // own handler (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1). AppHeader's
    // HeaderSearch focuses its input from a polled timer (focusSearchOnOpen),
    // which WebKit on a phone treats as script focus: no keyboard, and the
    // open pill stayed under the header islands (measured on dev DFI at
    // 375 / 393: the org name and the avatar drawn over "Search across
    // everything…"). The input is always mounted (the morph keeps it in the
    // DOM at opacity 0), so this gesture focuses it at once; the poll then
    // finds it focused. The header is the nearest ancestor that owns the pill.
    focusHeaderSearchInput(el) {
      let header = el && el.parent
      while (header && !header.HeaderSearch) header = header.parent
      const input = header?.HeaderSearch?.Form?.Input?.node
      if (input && typeof input.focus === 'function') input.focus({ preventScroll: true })
    }
  },
  tag: 'button',
  type: 'button',
  // A finger's target — the 44pt FLOOR (_touchTarget.js), not the 6px slop:
  // at the 14px iPad-portrait root this button is 25px, and 25 + 12 left a
  // 37px target (measured on dev DFI, 834px, touch simulated). These tabs
  // are bespoke buttons, so they state it themselves (2026-08-01, replaces
  // the injected selector block in workspace app.js).
  '@coarse': {
    position: 'relative',
    ...touchRow
  },
  width: 'B1',
  height: 'B1',
  minWidth: 'B1',
  round: '50%',
  align: 'center center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'caption',
  transition: '140ms defaultBezier background, 140ms defaultBezier color',
  'aria-label': (el) => el.call('polyglot', 'shell.headerSearch.placeholder'),
  title: (el) => el.call('polyglot', 'common.action.search'),
  // The button opens and closes the search (toggleHeaderSearchMorph), so it
  // says which: the state rides `aria-expanded`, re-read on every flip of the
  // flag it reports.
  'aria-expanded': (el, s) => (s.root?._headerSearchOpen ? 'true' : 'false'),
  subscribeTo: ['_headerSearchOpen'],
  ':hover': { background: 'hover', color: 'title' },
  ':active': { background: 'hover', color: 'title' },
  // N1 navigation (dropdowns spec 2026-09-30 §12): the LINE glyph set
  Icon: { extends: 'Icon', name: 'lnSearch', boxSize: 'iconSm' },
  // Act on MOUSEDOWN: re-renders (boot hydration, workspace updates) can
  // swap the button node between mousedown and mouseup, and a swapped
  // target kills the click event entirely — the "search sometimes doesn't
  // open" dead-click window. mousedown fires on the original target before
  // any swap. onClick stays the keyboard and assistive path.
  //
  // ONE TOGGLE PER PRESS. A press fires mousedown, then its click; while the
  // two only opened, the second was a no-op, and a toggle would undo the
  // first. So the mousedown notes its `detail` (`el.__searchPressDetail`) and
  // the click of the SAME press is spent. A pointer click carries a count
  // (`detail` ≥ 1); a keyboard click carries 0 and no mousedown before it;
  // an assistive click in Chrome sends mousedown and click, both 0. So a
  // click is the press's own when it has a count, or when its mousedown had
  // none — and a mousedown whose click a node swap ate never spends a later
  // keyboard click (count 1 against 0).
  onMousedown: (e, el, s) => {
    if (e.button !== 0) return
    el.__searchPressDetail = e.detail || 0
    el.scope.toggleHeaderSearchMorph(e, el, s)
  },
  onClick: (e, el, s) => {
    const pressed = el.__searchPressDetail
    el.__searchPressDetail = null
    if (pressed != null && (e.detail > 0 || pressed === 0)) {
      e.preventDefault()
      return
    }
    el.scope.toggleHeaderSearchMorph(e, el, s)
  }
}

// Shared Home action, placed below the toggle on a rail and in the tabs on a bar.
export const HeaderHomeBtn = {
  // The house tooltip, fed by `title` (TitleTooltipTrigger keeps the native one off).
  extends: 'NavbarRailControl',
  NavLabel: { text: '{{ shell.app.home | polyglot }}' },
  tag: 'button',
  type: 'button',
  // A finger's target — the 44pt FLOOR (_touchTarget.js), not the 6px slop:
  // at the 14px iPad-portrait root this button is 25px, and 25 + 12 left a
  // 37px target (measured on dev DFI, 834px, touch simulated). These tabs
  // are bespoke buttons, so they state it themselves (2026-08-01, replaces
  // the injected selector block in workspace app.js).
  '@coarse': {
    position: 'relative',
    ...touchRow
  },
  width: 'B1',
  height: 'B1',
  minWidth: 'B1',
  round: '50%',
  align: 'center center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'caption',
  transition: '140ms defaultBezier background, 140ms defaultBezier color',
  'aria-label': (el) => el.call('polyglot', 'shell.headerTabs.dashboardAriaLabel'),
  title: (el) => el.call('polyglot', 'shell.headerTabs.dashboard'),
  attr: { 'data-tab-path': '/' },
  ':hover': { background: 'hover', color: 'title' },
  '@mobileL': { display: 'none' },
  Icon: { extends: 'Icon', name: 'lnHome', boxSize: 'iconSm' },
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    el.router('/', el.getRoot())
  }
}

export const HeaderTabs = {
  scope: {
    // Active-tab wash lives on HeaderTab itself now, as the `selected`
    // design-system token on a reactive `background` factory (see HeaderTab
    // below). It used to be this imperative `var(--color-selected)` string
    // because the pill was painted by hand; the token form is what survives a
    // collection re-render (WS-NAV-ACTIVE-TAB-DOES-NOT-TRACK-ROUTE-1). The
    // VALUE is unchanged — `selected` IS `var(--color-selected)`
    // (DESIGN_LANGUAGE.md §4.3), the same wash the files/tickets sidebars, the
    // org-switcher rows and the /studio actives paint (2026-08-06).

    matchesPath(tabPath, p) {
      if (!tabPath) return false
      if (tabPath === '/') return p === '/'
      // Normalize BOTH sides through the legacy-alias map (tickets→tasks
      // rename 2026-08-06): a stored workspace navbar may still carry path
      // '/tickets' while the live URL is the canonical /tasks (or vice versa
      // on an old deep link) — either combination must still light the tab.
      const tab = canonicalRoutePath(tabPath)
      const path = canonicalRoutePath(p)
      return path === tab || path.startsWith(tab + '/')
    },

    // The route the active pill matches against — the PageSlot route
    // (`?page=`) when an app-window/module page is open, else the top-level
    // pathname.
    navMatchPath(root) {
      if (typeof window === 'undefined') return '/'
      // An open PageSlot sheet IS the foreground surface, so it decides the
      // active tab. The LIVE `_canvasSlot` is authoritative and must be
      // checked BEFORE the URL: `?page=` is only written while the
      // underlying route is home — `_writeSlotUrl` actively DELETES the
      // param on any other route — so a sheet opened over /studio or a
      // canvas project left the URL pointing at the route underneath it,
      // and that route's tab stayed lit while the user was looking at
      // something else entirely.
      const slot = root && root._canvasSlot
      if (slot) return String(slot).split('?')[0]
      // Strip any `/w/<org-slug>` prefix so tab matching works under
      // multi-workspace tabs; `?page=` is an unprefixed internal key.
      let p = stripWorkspacePrefix(window.location.pathname)
      try {
        const page = new URLSearchParams(window.location.search).get('page')
        // The param carries the FULL slot route, query included, since a
        // module record deep link puts its own params in it (A1-06). The tab
        // match is a PATH comparison, so strip them here exactly as the slot
        // branch above already does — otherwise a shared room link matches no
        // tab at all and the strip goes dark.
        if (page) p = String(page).split('?')[0].split('#')[0]
      } catch (_) {}
      return p
    },

    // Route → ONE root key. The active pill itself is DECLARATIVE (HeaderTab's
    // `background`/`color`/`aria-current` factories below); this publishes the
    // resolved route so those factories re-run on a navigation that touches no
    // other subscribed key. Called from the mount seed and from the two
    // `onWindow…` props — one body, three call sites, no closure to unbind.
    syncNavActivePath(el, s) {
      const root = (s && s.root) || el.state?.root
      if (!root) return
      const p = this.navMatchPath(root)
      if (root._navActivePath !== p) {
        root.update({ _navActivePath: p }, { preventFetch: true })
      }
    },

    // Effective assistant-dock squeeze — mirrors app.js's OWN derivation of
    // the <html data-assistant-dock> attribute exactly.
    assistantSqueezed(root) {
      if (!root?.assistantOpen) return false
      const dock =
        root.assistantDock === 'left' || root.assistantDock === 'minimized'
          ? root.assistantDock
          : 'right'
      return dock === 'left' || dock === 'right'
    },

    // Squeezed-header degradation (assistant docked narrows HeaderTabs'
    // island clamp): drop padding a step first, then let INACTIVE tabs'
    // minWidth floor collapse further so they ellipsize harder while the
    // ACTIVE tab keeps its full readable floor.
    applyTabSqueeze(node, root, active) {
      const squeezed = this.assistantSqueezed(root)
      node.style.padding = squeezed ? 'var(--spacing-Y) var(--spacing-Z2)' : ''
      node.style.minWidth = squeezed && !active ? '2.2em' : ''
    },

    // The tab arrangement is per-workspace: a workspace can override it via
    // root.workspaceSettings.navbar (set by applyWorkspaceConfig on
    // activation — e.g. GreenIG → Claims/Users/Calendar). Before
    // applyWorkspaceConfig lands on boot, read the same navbar from the
    // active workspace RECORD so the correct bar paints on the first render
    // (no default flash). Falls back to the house default
    // (Studio/Tasks/Files/Team). Each entry is { key, label, path, icon? }.
    // Length-guarded — a null OR empty saved navbar means "no
    // personalization", never "render zero tabs".
    //
    // Shared by BOTH tab renderings — the desktop text strip (Tabs) and the
    // @mobileL icon strip (MobileTabs) — so a workspace's personalization /
    // feature-flag gates / user prefs can never drift between the two.
    visibleNavTabs(root, el) {
      // A legacy custom pin's label: the built-in route's launcher KEY, from
      // the workspace's own tile list (searchApps.js launcherTitleKeyFor),
      // when this shell carries it.
      const pinOpts =
        el && typeof el.call === 'function' && el.context?.functions?.launcherTitleKeyFor
          ? { labelKeyFor: (p) => el.call('launcherTitleKeyFor', p) }
          : undefined
      // Gated on `pending`, NOT on `!ready`: `pending` is false once
      // completion is known to be impossible, so a user whose getWorkspace
      // fails gets the house default back instead of a permanently empty
      // navbar. See workspaceSettings.js's tri-state note.
      // THE COMM PROFILE (functions/commHost.js): a comm host shows its own
      // fixed tabs — the workspace's configured navbar names apps it does
      // not serve. Fixed, so no wait on the workspace record either. The
      // person's own pins merge in after the base list, so the result is
      // filtered again — a pinned /files lives on the workspace host.
      if (isCommHost()) {
        return applyNavbarTabPrefs(
          root,
          commNavTabs().filter((it) => navItemVisible(root, it)),
          pinOpts
        ).filter((it) => navItemVisible(root, it))
      }
      if (workspaceSettings(root).pending) return []

      const lc = (v) => (v == null ? '' : String(v).toLowerCase())
      // Earliest authoritative navbar for the FIRST paint after a reload —
      // read through THE accessor, never `root.workspace.settings` directly.
      const navFromActiveWorkspaceRecord = () => {
        const { settings, ready } = workspaceSettings(root)
        if (!ready) return null
        const ws = root?.workspace
        const nav = settings?.navbar
        if (!Array.isArray(nav) || !nav.length) return null
        const orgId = lc(root?.org?.id || root?.org?._id)
        const wsOrg = lc(ws.organization || ws.organizationId || ws.org)
        if (!orgId || !wsOrg || orgId !== wsOrg) return null
        return nav
      }

      const saved = root?.workspaceSettings?.navbar
      const configNav =
        Array.isArray(saved) && saved.length ? saved : navFromActiveWorkspaceRecord()
      if (configNav) {
        if (!featureEnabled(root, 'shell.moduleTabs')) return []
        return applyNavbarTabPrefs(
          root,
          configNav.filter((it) => navItemVisible(root, it)),
          pinOpts
        )
      }
      // Literal copy of HEADER_TABS (see the file-header FA204 comment) —
      // the canonical export stays untouched for its cross-file importers;
      // an in-file bare read of it here would be exactly as unresolvable
      // post-frank.toJSON as a module-local const.
      const headerTabs = [
        { key: 'studio', label: 'shell.app.studio', path: '/studio' },
        { key: 'files', label: 'shell.app.files', path: '/files' },
        { key: 'tickets', label: 'shell.app.tasks', path: '/tasks' },
        { key: 'team', label: 'shell.app.team', path: '/team-and-agents' }
      ]
      return applyNavbarTabPrefs(
        root,
        headerTabs.filter((it) => navItemVisible(root, it)),
        pinOpts
      )
    },

    // Current on-screen tab arrangement, from the DOM (the Tabs collection's
    // render order IS the arrangement the user sees).
    visibleTabPaths(tabsNode) {
      return Array.from(tabsNode?.querySelectorAll?.('[data-tab-path]') || [])
        .map((n) => n.getAttribute('data-tab-path'))
        .filter(Boolean)
    },

    // Which side of the hovered tab `e` should land on — before (left half)
    // or after (right half) of its own bounding box.
    dragAfter(e, el) {
      const rect = el.node?.getBoundingClientRect?.()
      return rect ? e.clientX > rect.left + rect.width / 2 : false
    },

    // A drag carrying the house entity-ref MIME (a search-dropdown app tile
    // — SearchAppsMenu.SearchAppTile stamps `kind: 'page'` + `meta.navPin`)
    // — the strip accepts it OUTSIDE reorder mode as a PIN drop.
    isRefDrag(el, e) {
      try {
        return !el.state?.root?._navbarReorder && !!el.call('hasDragRef', e)
      } catch (_) {
        return false
      }
    },

    // Commit a ref drop as a custom pin AT the drop position: ensure the
    // pinnedCustom row exists (skip when the path is already a visible tab
    // — then this is just a reorder), then persist the on-screen
    // arrangement with the path spliced in at the pointer's before/after
    // slot.
    async dropRefPin(e, el, s, targetPath, tabsNodeOverride) {
      let ref = null
      try {
        ref = el.call('readDragRef', e)
      } catch (_) {}
      const path = String(ref?.href || '').split(/[?#]/)[0]
      if (!path) return
      const root = s.root
      const tabsNode = tabsNodeOverride || el.parent?.node || el.node
      const visible = this.visibleTabPaths(tabsNode)
      try {
        if (
          !visible.includes(path) &&
          !navbarTabPrefs(root).pinnedCustom.some((c) => c && c.path === path)
        ) {
          await toggleCustomNavbarPin(root, { path, label: ref.title || path })
        }
        const paths = visible.filter((p) => p !== path)
        const at = targetPath ? paths.indexOf(targetPath) : -1
        if (at === -1) paths.push(path)
        else paths.splice(this.dragAfter(e, el) ? at + 1 : at, 0, path)
        el.call('saveNavbarTabOrder', paths)
      } catch (err) {
        console.warn('[HeaderTabs] ref pin failed:', err?.message || err)
      }
    },

    // A second press closes the launcher this chevron opened (the shell's
    // toggleSearchAppsDropdown, functions/searchApps.js): the plugin leaves a
    // press inside the anchor to the trigger, so an open-only press kept the
    // panel up (live on dev 286b17bdf, 2026-10-29: the rail chevron pressed
    // twice, the 480px launcher still up). A launcher another control opened
    // is replaced, as before.
    toggleAppsLauncher(e, el) {
      e.preventDefault()
      e.stopPropagation()
      // Anchor the apps panel to the whole tab-bar pill (el.parent) so it
      // sizes to the bar width, not the narrow arrow button (which left it
      // a thin sliver). On a left or right rail the pill is the tall tab
      // column, so the panel opens beside the rail at the chevron instead
      // (functions/navbarLayout.js navbarMenuAnchor): under the column it had
      // no room and flipped above it, 244px tall over the rail's head
      // (measured on dev 579269d80).
      try {
        // The chevron's tooltip must not sit over the launcher it opens (the
        // bell's contract): close one already on screen; `tooltipText` on
        // ArrowBtn answers nothing while the launcher is up.
        el.call('closeTooltip', el.tooltipId || el.key)
        el.call(
          'toggleSearchAppsDropdown',
          el.call('navbarMenuAnchor', 'bottom-center', el.parent || el)
        )
      } catch (err) {
        console.warn('[HeaderTabs] toggleSearchAppsDropdown unavailable:', err)
      }
    },

    // Is the apps launcher (the `searchApps` dropdown layer) up? Read from the
    // shell's one DropdownRoot, as searchApps.js itself reads it — the layer
    // sets no root key of its own.
    appsLauncherOpen(el) {
      const layer = el?.getRoot?.()?.DropdownRoot?.state?.layer
      return !!layer && layer.id === 'searchApps'
    }
  },
  // Primary navigation landmark — these tabs switch routes (Studio/Infra/…),
  // so expose the bar as the page's primary navigation for assistive tech.
  role: 'navigation',
  'aria-label': (el) => el.call('polyglot', 'shell.headerTabs.primaryNav'),
  // Centered absolute pill — same anchor as the HeaderSearch it replaces so
  // the left/right header islands don't shift.
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  // Clamp against the measured islands (AppHeader publishes --hdr-left-w /
  // --hdr-right-w from a ResizeObserver): dead-centering is the design
  // contract, but the pill must never slide under the left/right islands
  // when the header narrows — small screens, the docked assistant (the
  // shell CSS shrinks the header by --assistant-width), or a workspace
  // navbar carrying many module tabs (2026-07-27 per Nika "navbar looks
  // jammed … add margin on middle section so it does not stick"). The
  // symmetric 2×max() keeps true centering with an --spacing-A clearance
  // on both sides; the tabs inside ellipsize once the clamp binds (see
  // Tabs / HeaderTab minWidth: 0 + textOverflow below).
  maxWidth: 'calc(100% - 2 * (max(var(--hdr-left-w), var(--hdr-right-w)) + var(--spacing-A)))',
  flow: 'x',
  align: 'center center',
  // Gap between the icons / tabs — a touch more breathing room (V → X) so the
  // home + search icons aren't cramped against each other (2026-06-14 per Nika
  // "increase gap between icons a little bit").
  gap: 'X',
  // 2026-06-10 per Nika: slightly tighter horizontal (A → Z2); the tabs
  // inside gained vertical padding instead.
  padding: 'V Z2',
  round: 'radiusPill',
  // The header's pill: the skins' `pill` theme (AppHeader.js HeaderLeft note)
  // — the navbar glass in the solid skin, the card glass in the ambient one.
  theme: 'pill',
  borderWidth: '0',
  zIndex: '5',
  transition: '180ms defaultBezier opacity, 180ms defaultBezier transform',
  // tabletMOnly (not tabletM): this block's gap/padding disagree with
  // '@mobileL' below on the SAME properties. A bare max-width tabletM
  // (≤1280px) still matches at 390px, and the atomic CSS engine's global
  // class-insertion order — not which block is logically narrower — decides
  // the winner (live-verified on this exact component; see AppHeader.js's
  // '@tabletSOnly' comment for the full mechanism and
  // designSystem/media.js's tabletSOnly/tabletMOnly tokens). tabletMOnly
  // (1280–769px) never overlaps '@mobileL' (≤768px), so it can never win a
  // tie it shouldn't.
  '@tabletMOnly': {
    gap: '0',
    padding: 'W2 Z'
  },
  '@mobileL': {
    gap: 'Z',
    padding: 'V Z'
  },

  // Right-click on the pill BACKGROUND (not a tab): reset-only menu. This is
  // the escape hatch once every tab has been removed — with zero tabs there
  // is nothing left to right-click for "Reset navbar items". No-op while the
  // navbar is uncustomized (an empty menu must never open).
  onContextmenu: (e, el, s) => {
    const prefs = navbarTabPrefs(s.root)
    // Mirror navbarTabMenuItems' own `customized` union EXACTLY (incl.
    // pinnedCustom) — a stricter gate here made the strip menu dead for
    // users whose only customization is a custom-pinned search result.
    if (
      !prefs.hidden.length &&
      !prefs.pinned.length &&
      !prefs.order.length &&
      !prefs.pinnedCustom.length
    ) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    s.root.update(
      {
        contextDropdown: {
          open: true,
          x: e.clientX,
          y: e.clientY,
          itemsFn: 'navbarTabMenuItems',
          actionFn: 'navbarTabMenuAction',
          navTab: null
        }
      },
      { preventFetch: true }
    )
  },

  // FA503 / Rule 64 — the four listeners this strip needs are DECLARED, so
  // DOMQL registers each exactly once when the element takes its node and
  // removes all four in dispose(). The pre-fix shape armed them from
  // `onCreate` behind an `_escBound` latch and mirrored every handler onto
  // `el._escHandler` / `el._reorderOutsideHandler` / `el._syncActive` purely
  // so `onRemove` could take them off again — four listeners, three fields
  // and two hooks of bookkeeping for what the props below state directly.

  // Escape closes the search morph back to the tabs — unless a surface on
  // top already handled this Escape (a launcher tile's or a result row's
  // menu, a dialog: they preventDefault the key they spend). Top overlay
  // first: the search stays for the next Escape
  // (DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1, measured live on dev a9783341a:
  // one Escape on a tile's menu closed the menu, the launcher and the search).
  // A close SPENDS the key (preventDefault): one Escape, one layer — the press
  // that closed the search (and its launcher) used to reach a lower document
  // layer too, e.g. home customize mode under an open search (fw-wl's live
  // Escape checks on dev e004fe4ec, 2026-09-30).
  //
  // TOP LAYER FIRST: a dialog or a dropdown ABOVE the search (the search's own
  // launcher included) owns this Escape — the ONE reader (shared
  // functions/shellOverlayOpen.js) names it, and the search stays for the
  // next press. Its window-level listener runs AFTER this document one, so
  // without the check the search under an open dialog closed first (live on
  // dev 2ffb10c23, 2026-10-01). The main thread sits BELOW the search.
  onDocumentKeydown: (ev, el, s) => {
    if (ev.key !== 'Escape' || ev.defaultPrevented) return
    const layer =
      typeof el?.call === 'function' &&
      typeof el?.context?.functions?.shellOverlayOpen === 'function'
        ? el.call('shellOverlayOpen')
        : null
    if (['modalRoot', 'dropdown', 'phoneNav', 'mobileRail'].includes(layer)) return
    if (s.root?._navbarReorder) {
      s.root.update({ _navbarReorder: false }, { preventFetch: true })
      if (typeof ev.preventDefault === 'function') ev.preventDefault()
      return
    }
    if (s.root?._headerSearchOpen) {
      s.root.update({ _headerSearchOpen: false })
      if (typeof ev.preventDefault === 'function') ev.preventDefault()
    }
  },

  // Any press OUTSIDE the tab bar exits reorder mode — the natural "done"
  // gesture (mirrors the dropdown plugin's outside-close contract).
  onDocumentPointerdown: {
    capture: true,
    handler: (ev, el, s) => {
      if (!s.root?._navbarReorder) return
      if (el.node && ev.target && el.node.contains(ev.target)) return
      s.root.update({ _navbarReorder: false }, { preventFetch: true })
    }
  },

  onWindowPopstate: (ev, el, s) => el.scope.syncNavActivePath(el, s),
  onWindowLocationchange: (ev, el, s) => el.scope.syncNavActivePath(el, s),

  onCreate: (el, s) => {
    if (typeof document === 'undefined') return
    // Route → ONE root key. The active pill itself is DECLARATIVE (HeaderTab's
    // `background`/`color`/`aria-current` factories below); this handler only
    // publishes the resolved route so those factories re-run on a navigation
    // that touches no other subscribed key. history.pushState is patched by the
    // shell to emit `locationchange`, so el.router() reaches this too.
    //
    // WS-NAV-ACTIVE-TAB-DOES-NOT-TRACK-ROUTE-1 — the pill USED to be painted
    // imperatively here (and from each tab's create/update hook) by matching
    // `n.getAttribute('data-tab-path')`. Measured live on vault.pr (staging
    // shell 8b492dec2): when the Tabs collection re-renders with a DIFFERENT
    // list — the module tabs (`/vault-*`) inject a beat after boot and push
    // `/tasks`/`/files` down the strip — DOMQL re-applies every prop on the
    // REUSED child nodes (the `data-tab-path` attr factory re-emits) but fires
    // NO create/update hook on them, so neither imperative writer ran again and
    // the inline wash stayed pinned to the node INDEX that was active before
    // the swap. A `?page=/vault-rooms` deep link therefore lit "Tasks" until
    // the first in-app navigation re-ran this handler. Props re-apply on that
    // swap; hooks do not — so the active state has to BE a prop.
    // Seed the key once at mount; `onWindowPopstate` / `onWindowLocationchange`
    // above carry every later navigation.
    setTimeout(() => el.scope.syncNavActivePath(el, s), 0)
  },

  // ── Home icon — routes to the dashboard ('/') (2026-06-19 per Nika, restored) ──
  // Hidden at @mobileL: the merged deck folds Home into MobileTabs as its
  // leading (always-first, never scrolled-past-since-it-starts-at-rest) entry
  // instead — see MobileTabs.children below. Desktop/tablet keep this
  // standalone icon unchanged.
  HomeBtn: {
    extends: 'HeaderHomeBtn',
    hide: (el, s) => !!(el.call('navbarLayoutRead', s.root) || {}).rail
  },

  // ── Search icon — morphs HeaderSearch in. Bare-key auto-extend from the
  // top-level SearchBtn export below (2026-08-02) — a SECOND mount lives in
  // HeaderRight (AppHeader.js), visible ONLY at @mobileL, so search can sit
  // in the trailing zone beside the avatar there (brief: "Trailing zone:
  // search + avatar") while this desktop/tablet position stays put. Hidden
  // here at @mobileL so the two never show at once. ──
  SearchBtn: {
    hide: (el, s) => !!(el.call('navbarLayoutRead', s.root) || {}).rail,
    '@mobileL': { display: 'none' }
  },

  // The app icons stand apart from the strip's own (Home, Search · the
  // launcher): a hairline on each side of them (NavTabsDivider).
  NavTabsDivider_start: {},

  // ── Canvas tabs ──
  Tabs: {
    // The edge-fade row (STRIP_ROW above): min-width 0, sideways scroll with
    // no scrollbar, overscroll contained, the fade, the current tab kept
    // whole — its own keys below win where they differ.
    ...STRIP_ROW,
    flow: 'x',
    align: 'center',
    gap: 'V',
    // THE ICON ROW (the look's `navbarIcons`, on by default): the app icons breathe — the gap between their plates
    // is the left island's own between its icons (Nika 2026-10-23: "add more spacing in between").
    class: { iconRow: (el, s) => !!(el.call('homeLookRead', s && s.root) || {}).navbarIcons },
    '&.iconRow': { gap: 'Z' },
    // ON A RAIL (functions/navbarLayout.js; AppHeader's root note — the rail
    // is an attribute and its rules select on it, never `isX` cases): the
    // tabs stack and scroll down the column instead of across — no side fade:
    // a column never overflows sideways, so the row's own observer sets no
    // fade attribute (AppScrollRow) and no mask rule is needed here; wide
    // (expanded), each tab row runs the rail's width.
    'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s && s.root) || {}).railMode,
    '&[data-nav-rail]': {
      flexDirection: 'column',
      flex: '0 1 auto',
      minHeight: '0',
      overflowX: 'hidden',
      overflowY: 'auto',
      gap: 'Z'
    },
    '&[data-nav-rail="wide"]': { alignItems: 'stretch' },
    // The flexible member of the pill — the icon buttons keep their fixed
    // B1 footprint, this collection absorbs the root maxWidth clamp. Tabs
    // keep their NATURAL width (no per-item "…" — 2026-08-07 per Nika
    // "instead of ... on each item, just show them and fade away with
    // shadow whats not fitting"): when the clamp binds, the STRIP overflows
    // and scrolls on every pointer kind, with the cut-off edge FADED — a
    // scrolled-off label reads as "more this way", never as a cropped
    // layout. The row owns no state: a scroll never writes the root state,
    // and the tabs below keep reading `s.root` for everything they share
    // with the shell.
    flexShrink: '1',
    '@tabletS': {
      gap: '0'
    },
    '@mobileL': { display: 'none' },
    // Strip-level ref-drop acceptance: a tile dropped on empty strip space
    // (not on a tab — those handle their own positioned drop and stop
    // propagation) pins at the END. dragleave clears the insertion
    // indicator for aborted external drags — the tile's own dragend can't
    // reach this component's state.
    onDragover: (e, el, s) => {
      if (!el.scope.isRefDrag(el, e)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    },
    onDrop: (e, el, s) => {
      if (!el.scope.isRefDrag(el, e)) return
      e.preventDefault()
      s.root.update(
        { _navbarDragOverPath: null, _navbarDragOverAfter: false },
        { preventFetch: true }
      )
      el.scope.dropRefPin(e, el, s, null, el.node)
    },
    onDragleave: (e, el, s) => {
      if (el.node && e.relatedTarget && el.node.contains(e.relatedTarget)) return
      if (s.root?._navbarDragOverPath) {
        s.root.update(
          { _navbarDragOverPath: null, _navbarDragOverAfter: false },
          { preventFetch: true }
        )
      }
    },
    // List logic lives in scope.visibleNavTabs — shared verbatim with
    // MobileTabs' icon strip below.
    children: (el, s) => el.scope.visibleNavTabs(s.root, el),
    childExtends: 'HeaderTab',
    childrenAs: 'state',
    subscribeTo: ['_featureFlags', 'workspace', 'workspaceReady', 'workspaceSettings', 'userPrefs']
  },

  // ── Mobile LABEL strip — @mobileL "one deck" merge (2026-08-02 per Nika
  // "make navbars on mobile smarter, more compact … native-like"; revised
  // same day after live-probe findings). Same list _visibleNavTabs feeds
  // Tabs above; visible where Tabs hides, so every configured destination is
  // one tap away instead of folded behind the apps-launcher chevron
  // ("nothing disappears"). TEXT, not icons: a first pass rendered icons,
  // but on a real (module-heavy) workspace every config-driven nav row
  // shares the SAME generic fallback glyph — 13 identical grey squares,
  // strictly worse than the desktop text tabs this replaces. Config-driven
  // `workspaceSettings.navbar` rows carry no icon metadata and HEADER_TABS'
  // 4 built-in defaults are the exception, not the norm for a real customer
  // workspace, so a labels-only strip is the one treatment that's always
  // legible — it reuses the SAME label strings Tabs already renders, no new
  // metadata needed, and a scrolling text-tab strip is the standard native
  // pattern on both iOS and Android for exactly this.
  //
  // Owns the WHOLE center zone now: HomeBtn/SearchBtn/ArrowBtn all step
  // aside at this breakpoint (their own '@mobileL' overrides) instead of
  // competing with this flex:1 strip for HeaderTabs' width — three fixed
  // B1 buttons + their gaps were eating ~55% of the available 212px, down to
  // an 80px scroll window (live-measured on a 13-item workspace). Home
  // folds in as the ALWAYS-FIRST synthetic entry below (never scrolled past
  // at rest, since the strip starts at scrollLeft 0); Search relocates to
  // the trailing zone beside the avatar (the brief's own "Trailing zone:
  // search + avatar" — also returns real width, since HeaderTabs' flex:1
  // shrinks by whatever HeaderRight grows, only worthwhile if it lands
  // Search somewhere useful, which trailing is); Arrow (apps launcher) is
  // hidden outright — HeaderSearch's Input already opens the SAME apps
  // dropdown on focus/hover (HeaderSearch.js `onFocus`/`onInput` →
  // `openSearchAppsDropdown`), and SearchBtn's own tap already focuses that
  // input (`_openHeaderSearchMorph`), so the full catalog stays one
  // (different) tap away without spending 29px on a second entry point.
  MobileTabs: {
    // The same edge-fade row as Tabs (it shipped the same 1.25px `V` crop);
    // inert while this strip is display:none.
    ...STRIP_ROW,
    display: 'none',
    '@mobileL': {
      display: 'flex',
      flex: '1 1 0%',
      minWidth: '0',
      flow: 'x',
      align: 'center',
      gap: 'V',
      overflowX: 'auto',
      overflowY: 'hidden',
      overscrollBehaviorX: 'contain',
      scrollSnapType: 'x proximity',
      // Hidden-scrollbar pair (InstallableRail idiom, mirrors Tabs' own
      // '@coarse' block above) — the strip swipes like a native tab bar, no
      // scrollbar chrome inside the deck.
      scrollbarWidth: 'none',
      '::-webkit-scrollbar': { display: 'none' }
    },
    childExtends: 'MobileTab',
    childrenAs: 'state',
    // Home prepended — a fixed system anchor, not part of the user's
    // configurable navbar, so it always leads (mirrors HomeBtn's own fixed
    // leading position on desktop) rather than being subject to
    // applyNavbarTabPrefs' pin/hide/reorder.
    children: (el, s) => [
      { key: 'home', label: 'shell.app.home', path: '/' },
      ...el.scope.visibleNavTabs(s.root, el)
    ],
    subscribeTo: ['_featureFlags', 'workspace', 'workspaceReady', 'workspaceSettings', 'userPrefs']
  },

  NavTabsDivider_end: {},

  // ── Arrow-down — apps launcher dropdown. Hidden at @mobileL — see the
  // MobileTabs comment above for why the launcher stays reachable (via
  // Search) without this spending width in the merged deck. ──
  ArrowBtn: {
    // The house tooltip, fed by `title` (TitleTooltipTrigger keeps the native one off).
    extends: 'NavbarRailControl',
    NavLabel: { text: '{{ shell.headerTabs.openApps | polyglot }}' },
    tag: 'button',
    type: 'button',
    // A finger's target — the 44pt FLOOR (_touchTarget.js), not the 6px slop:
    // at the 14px iPad-portrait root this button is 25px, and 25 + 12 left a
    // 37px target (measured on dev DFI, 834px, touch simulated). These tabs
    // are bespoke buttons, so they state it themselves (2026-08-01, replaces
    // the injected selector block in workspace app.js).
    '@coarse': {
      position: 'relative',
      ...touchRow
    },
    width: 'B1',
    height: 'B1',
    minWidth: 'B1',
    round: '50%',
    align: 'center center',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'caption',
    transition: '140ms defaultBezier background, 140ms defaultBezier color',
    'aria-label': (el) => el.call('polyglot', 'shell.headerTabs.openApps'),
    'aria-haspopup': 'menu',
    // Whether the menu it opens is up (the popup trigger's second half): the
    // open layer's root mirror (`_dropdownLayer`, functions/index.js
    // openDropdown: set by the layer's open hook, cleared by its close hook).
    // Read off the plugin's layer it stayed "false" while the launcher was up
    // (live on dev 286b17bdf, 2026-10-29): the layer reaches only the
    // DropdownRoot's own children, and the root mirror reaches this button.
    ariaExpanded: (el, s) => (s.root?._dropdownLayer === 'searchApps' ? 'true' : 'false'),
    title: (el) => el.call('polyglot', 'shell.headerTabs.apps'),
    // Read at SHOW time: nothing to show while the launcher is up, so "Apps"
    // never paints over the panel it just opened (live on dev 0ffad3a40,
    // 1440 x 813: the tooltip sat on the launcher's top-right corner;
    // DESIGN_SPEC §5.9 Tooltip — it hides when its trigger opens a popover).
    tooltipText: (el) =>
      el.scope.appsLauncherOpen(el) || (el.call('navbarLayoutRead', el.state.root) || {}).expanded
        ? ''
        : el.call('polyglot', 'shell.headerTabs.apps'),
    ':hover': { background: 'hover', color: 'title' },
    '@mobileL': { display: 'none' },
    // A comm host has no launcher: its apps are the tabs, and the rest lives
    // on the workspace host (WorkspaceBtn below).
    hide: (el) => el.call('isCommHost'),
    Icon: { extends: 'Icon', name: 'lnCaret', boxSize: 'iconSm' },
    // Same mousedown-first contract as SearchBtn (dead-click window) — and
    // running in the bubble phase means the dropdown plugin's capture-phase
    // outside-mousedown close always resolves BEFORE this open: deterministic
    // close-then-open instead of the old race.
    onMousedown: (e, el) => {
      if (e.button === 0) el.scope.toggleAppsLauncher(e, el)
    },
    // ONE toggle per press: a pointer press toggles on its mousedown, so the
    // click after it only swallows the event; a click with no press before
    // it (Enter or Space on the focused button, detail 0) toggles itself.
    onClick: (e, el) => {
      if (e.detail > 0) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      el.scope.toggleAppsLauncher(e, el)
    }
  },

  // ── Open workspace — a comm host only (functions/commHost.js), where it
  // takes the launcher's place: the whole workspace, on the workspace host
  // of the same tier. Shown on a real comm host only — a simulated one
  // (`?comm=1`) has no workspace host to open. ──
  WorkspaceBtn: {
    extends: 'NavbarRailControl',
    tag: 'button',
    type: 'button',
    '@coarse': {
      position: 'relative',
      ...touchRow
    },
    width: 'B1',
    height: 'B1',
    minWidth: 'B1',
    round: '50%',
    align: 'center center',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'caption',
    transition: '140ms defaultBezier background, 140ms defaultBezier color',
    'aria-label': (el) => el.call('polyglot', 'shell.headerTabs.openWorkspace'),
    title: (el) => el.call('polyglot', 'shell.headerTabs.openWorkspace'),
    ':hover': { background: 'hover', color: 'title' },
    '@mobileL': { display: 'none' },
    hide: (el) => !el.call('isCommHost') || !el.call('workspaceHostUrl', '/'),
    Icon: { extends: 'Icon', name: 'lnApps', boxSize: 'iconSm' },
    onClick: (e, el) => {
      e.preventDefault()
      e.stopPropagation()
      el.call('openOnWorkspaceHost', el, '/')
    }
  }
}

// One text tab. `state` is a HEADER_TABS entry ({ key, label, path }).
// THE APP ICONS STAND APART (Nika 2026-10-23: "isolate this navbar icons from left and right icons"): while the
// navbar tabs are icons (the look's `navbarIcons`, on by default), a hairline in the house `line` ink on each side
// of them keeps the app glyph plates from reading as one run with the strip's own icons — Home and Search before
// them, the launcher after. Decorative: no role, hidden from assistive tech; hidden with the text tabs, with no app
// tabs, and on a phone (the strip there is MobileTabs). Rule 66: a hairline the strip always has, never a layout
// shift — it shows and hides with the look, not with hover.
export const NavTabsDivider = {
  aria: { hidden: 'true' },
  flexShrink: '0',
  width: '1px',
  height: 'A',
  marginInline: 'Z',
  background: 'line',
  // Bar separators follow the icon preference; rails suppress them below.
  hide: (el, s) =>
    !(
      (el.call('homeLookRead', s && s.root) || {}).navbarIcons ||
      (el.call('navbarLayoutRead', s && s.root) || {}).rail
    ) ||
    !(el.parent && el.parent.scope && typeof el.parent.scope.visibleNavTabs === 'function'
      ? el.parent.scope.visibleNavTabs(s && s.root, el.parent).length
      : 0),
  // Rails use a continuous gap between controls instead of separators.
  'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s && s.root) || {}).railMode,
  '&[data-nav-rail]': { display: 'none' },
  subscribeTo: ['userPrefs', 'workspace', 'workspaceReady', 'workspaceSettings', '_featureFlags'],
  '@mobileL': { display: 'none' }
}

export const HeaderTab = {
  // the one state logic of the glyph plate (hover / keyboard focus / current invert it, current shows the dot);
  // the house tooltip carries the tab's name while the tab is an icon (`title` below)
  extends: ['NavbarRailControl', 'GlyphPlateHolder'],
  scope: {
    matchesPath(tabPath, p) {
      if (!tabPath) return false
      if (tabPath === '/') return p === '/'
      const tab = canonicalRoutePath(tabPath)
      const path = canonicalRoutePath(p)
      return path === tab || path.startsWith(tab + '/')
    },
    navMatchPath(root) {
      if (typeof window === 'undefined') return '/'
      const slot = root && root._canvasSlot
      if (slot) return String(slot).split('?')[0]
      let p = stripWorkspacePrefix(window.location.pathname)
      try {
        const page = new URLSearchParams(window.location.search).get('page')
        // The param carries the FULL slot route, query included, since a
        // module record deep link puts its own params in it (A1-06). The tab
        // match is a PATH comparison, so strip them here exactly as the slot
        // branch above already does — otherwise a shared room link matches no
        // tab at all and the strip goes dark.
        if (page) p = String(page).split('?')[0].split('#')[0]
      } catch (_) {}
      return p
    },
    assistantSqueezed(root) {
      if (!root?.assistantOpen) return false
      const dock =
        root.assistantDock === 'left' || root.assistantDock === 'minimized'
          ? root.assistantDock
          : 'right'
      return dock === 'left' || dock === 'right'
    },
    applyTabSqueeze(node, root, active, icon) {
      // An icon tab is already its smallest: the squeeze would only bend the circle.
      const squeezed = !icon && this.assistantSqueezed(root)
      node.style.padding = squeezed ? 'var(--spacing-Y) var(--spacing-Z2)' : ''
      node.style.minWidth = squeezed && !active ? '2.2em' : ''
    },
    // Route-match only, no styling opinion — read by this tab's declarative
    // background/color/aria-current factories AND by the squeeze sync below,
    // so the two can never disagree about what counts as "active".
    //
    // The route comes from THIS element's own state (`s.path`), never from
    // `node.getAttribute('data-tab-path')`: a collection re-render swaps the
    // state on a REUSED node, and any matcher that reads the DOM attribute
    // instead of the state is one render behind
    // (WS-NAV-ACTIVE-TAB-DOES-NOT-TRACK-ROUTE-1 — see HeaderTabs.onCreate).
    //
    // The LONGEST matching tab of the strip wins (NAVBAR-ACTIVE-LONGEST-
    // MATCH-1): an app's nested page — /validators/networks — lit BOTH
    // "Validators" (/validators, a prefix match) and "Networks". `el` gives
    // the strip's own list (the row's visibleNavTabs — exactly what it
    // renders); without it the plain prefix match stands.
    tabActive(s, el) {
      const p = this.navMatchPath(s && s.root)
      if (!this.matchesPath(s && s.path, p)) return false
      const row = el && el.parent
      const list =
        row && row.scope && typeof row.scope.visibleNavTabs === 'function'
          ? row.scope.visibleNavTabs(s.root, row)
          : null
      if (!Array.isArray(list)) return true
      const mine = canonicalRoutePath(s.path)
      return !list.some((t) => {
        const other = t && t.path ? canonicalRoutePath(t.path) : ''
        return other.length > mine.length && this.matchesPath(t.path, p)
      })
    },
    // Squeeze-only self-heal. The active WASH is declarative (below); the
    // assistant-dock squeeze stays imperative because it writes `padding` /
    // `minWidth`, which this component also sets inside '@tabletM' /
    // '@tabletS' media blocks — a base-level factory for those two would race
    // the media rules on emission order (FW-ATOMIC-CSS-MEDIA-RULE-LOSES-TO-
    // BASE-BY-EMISSION-ORDER-1). An inline write outranks both, which is
    // exactly what the squeeze wants.
    applyTabActive(el) {
      const node = el.node
      if (!node) return
      const root = el.getRoot?.()?.state
      const icon = !!(el.call('homeLookRead', el.state && el.state.root) || {}).navbarIcons
      this.applyTabSqueeze(node, root, this.tabActive(el.state, el), icon)
    },
    // ── Long-press → reorder mode ──
    LP_MS: 450,
    LP_TRAVEL: 6,
    lpStart(e, el, s) {
      if (e.button !== 0 || s.placeholder || !s.path) return
      if (s.root?._navbarReorder) return // already in the mode — gestures are drags
      el._lpFired = false
      el._lpMoved = false
      el._lpX = e.clientX
      el._lpY = e.clientY
      try {
        el.node?.setPointerCapture?.(e.pointerId)
      } catch (_) {
        /* capture is best-effort */
      }
      el._lpTimer = setTimeout(() => {
        el._lpTimer = null
        el._lpFired = true
        try {
          el.node?.releasePointerCapture?.(e.pointerId)
        } catch (_) {}
        s.root.update({ _navbarReorder: true }, { preventFetch: true })
      }, this.LP_MS)
    },
    lpCancel(el) {
      if (el._lpTimer) {
        clearTimeout(el._lpTimer)
        el._lpTimer = null
      }
    },
    lpMove(e, el) {
      if (!el._lpTimer && !el._lpFired) return
      const dx = Math.abs(e.clientX - (el._lpX || 0))
      const dy = Math.abs(e.clientY - (el._lpY || 0))
      if (dx > this.LP_TRAVEL || dy > this.LP_TRAVEL) {
        el._lpMoved = true
        this.lpCancel(el)
      }
    },
    lpEnd(e, el, s) {
      const held = el._lpFired
      this.lpCancel(el)
      try {
        el.node?.releasePointerCapture?.(e.pointerId)
      } catch (_) {}
      if (held || el._lpMoved) {
        el._lpFired = false
        el._lpMoved = false
        return
      }
      this.openTab(e, el, s)
    },
    // Open a navbar tab's route.
    openTab(e, el, s) {
      if (s.placeholder || !s.path) return
      if (s.root?._navbarReorder) return
      e.preventDefault()
      if (s.path === '/') {
        el.router('/', el.getRoot())
        return
      }
      try {
        el.call('openAppPage', s.path, { preferSlot: true, split: e.metaKey || e.ctrlKey })
        return
      } catch (_) {}
      el.router(s.path, el.getRoot())
    },
    // Current on-screen tab arrangement, from the DOM.
    visibleTabPaths(tabsNode) {
      return Array.from(tabsNode?.querySelectorAll?.('[data-tab-path]') || [])
        .map((n) => n.getAttribute('data-tab-path'))
        .filter(Boolean)
    },
    dragAfter(e, el) {
      const rect = el.node?.getBoundingClientRect?.()
      return rect ? e.clientX > rect.left + rect.width / 2 : false
    },
    isRefDrag(el, e) {
      try {
        return !el.state?.root?._navbarReorder && !!el.call('hasDragRef', e)
      } catch (_) {
        return false
      }
    },
    // Commit a ref drop as a custom pin AT the drop position — duplicated
    // verbatim from HeaderTabs' own scope (this component's onDrop needs
    // the SAME logic when the drop target is a specific tab rather than
    // empty strip space).
    async dropRefPin(e, el, s, targetPath, tabsNodeOverride) {
      let ref = null
      try {
        ref = el.call('readDragRef', e)
      } catch (_) {}
      const path = String(ref?.href || '').split(/[?#]/)[0]
      if (!path) return
      const root = s.root
      const tabsNode = tabsNodeOverride || el.parent?.node || el.node
      const visible = this.visibleTabPaths(tabsNode)
      try {
        if (
          !visible.includes(path) &&
          !navbarTabPrefs(root).pinnedCustom.some((c) => c && c.path === path)
        ) {
          await toggleCustomNavbarPin(root, { path, label: ref.title || path })
        }
        const paths = visible.filter((p) => p !== path)
        const at = targetPath ? paths.indexOf(targetPath) : -1
        if (at === -1) paths.push(path)
        else paths.splice(this.dragAfter(e, el) ? at + 1 : at, 0, path)
        el.call('saveNavbarTabOrder', paths)
      } catch (err) {
        console.warn('[HeaderTabs] ref pin failed:', err?.message || err)
      }
    },
    // Live dragover tracking for the insertion-bar indicator.
    dragOver(e, el, s) {
      if (this.isRefDrag(el, e)) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
        const after = this.dragAfter(e, el)
        if (s.root._navbarDragOverPath !== s.path || s.root._navbarDragOverAfter !== after) {
          s.root.update(
            { _navbarDragOverPath: s.path, _navbarDragOverAfter: after },
            { preventFetch: true }
          )
        }
        return
      }
      if (!s.root?._navbarReorder) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      if (!s.path || s.path === s.root._navbarDragPath) return
      const after = this.dragAfter(e, el)
      if (s.root._navbarDragOverPath === s.path && s.root._navbarDragOverAfter === after) return
      s.root.update(
        { _navbarDragOverPath: s.path, _navbarDragOverAfter: after },
        { preventFetch: true }
      )
    },
    // Drop a dragged tab beside the drop target, persist the full new
    // arrangement.
    dropTab(e, el, s) {
      if (this.isRefDrag(el, e)) {
        e.preventDefault()
        e.stopPropagation()
        s.root.update(
          { _navbarDragPath: null, _navbarDragOverPath: null, _navbarDragOverAfter: false },
          { preventFetch: true }
        )
        this.dropRefPin(e, el, s, s.path)
        return
      }
      if (!s.root?._navbarReorder) return
      e.preventDefault()
      e.stopPropagation()
      s.root.update(
        { _navbarDragPath: null, _navbarDragOverPath: null, _navbarDragOverAfter: false },
        { preventFetch: true }
      )
      const dragged = e.dataTransfer?.getData('text/nav-tab')
      const target = s.path
      if (!dragged || !target || dragged === target) return
      const paths = this.visibleTabPaths(el.parent?.node).filter((p) => p !== dragged)
      const at = paths.indexOf(target)
      if (at === -1) return
      const after = this.dragAfter(e, el)
      paths.splice(after ? at + 1 : at, 0, dragged)
      try {
        el.call('saveNavbarTabOrder', paths)
      } catch (err) {
        console.warn('[HeaderTabs] order save failed:', err?.message || err)
      }
    }
  },
  tag: 'button',
  type: 'button',
  // Positioning context for InsertBar (drag insertion indicator, below) —
  // relative with no offsets doesn't affect normal flex-item layout.
  position: 'relative',
  // The physical 44px row stays inside the scroll strip's clip box and
  // leaves every neighboring tab its own touch target.
  '@coarse': {
    position: 'relative',
    ...touchRow
  },
  // Each tab exposes its route via data-tab-path — read by the reorder
  // drag/drop code and by tests, NOT by the active matcher (which reads the
  // element's own state — see scope.tabActive).
  attr: { 'data-tab-path': (el, s) => s.path },
  // The primary navigation landmark's children expose the CURRENT destination
  // — aria-current is what tells assistive tech which one that is. Same form
  // MobileTab below already carries.
  'aria-current': (el, s) => (el.scope.tabActive(s, el) ? 'page' : 'false'),
  // Opening/closing a sheet over a NON-home route rewrites no URL, so neither
  // popstate nor locationchange fires — subscribing to the slot key is what
  // re-runs the active factories when the foreground surface changes without a
  // navigation. `_navActivePath` is the route HeaderTabs.onCreate publishes on
  // popstate/locationchange — the trigger for a plain el.router() navigation,
  // which touches no other key here. `_navbarReorder` re-fires the reactive
  // draggable/outline factories when the mode flips.
  subscribeTo: [
    '_canvasSlot',
    '_navActivePath',
    '_navbarReorder',
    '_navbarDragPath',
    '_navbarDragOverPath',
    '_navbarDragOverAfter',
    'assistantOpen',
    'assistantDock'
  ],
  onCreate: (el) => {
    el.scope.applyTabActive(el)
    el._wasActive = el.scope.tabActive(el.state, el)
  },
  // The current destination is never left cut by the strip: the update in
  // which this tab BECOMES active (a navigation) brings it to the strip's
  // middle if the strip cuts it — the row's scope.edgeFadeBecameCurrent. An
  // update that keeps it active (the squeeze, a drag) scrolls nothing.
  onUpdate: (el) => {
    el.scope.applyTabActive(el)
    const active = el.scope.tabActive(el.state, el)
    if (active && !el._wasActive) el.parent?.scope?.edgeFadeBecameCurrent?.(el.parent, el.node)
    el._wasActive = active
  },

  // Reorder-mode affordances: dashed outline + grab cursor + native drag.
  // STRING values — a boolean true renders `draggable=""`, which HTML treats
  // as invalid → 'auto' → buttons stay non-draggable; only the literal
  // 'true' enables native drags.
  draggable: (el, s) => (s.root?._navbarReorder ? 'true' : 'false'),
  outline: (el, s) => (s.root?._navbarReorder ? '1px dashed' : 'none'),
  outlineColor: 'caption',
  outlineOffset: '-1px',
  // Lift on the tab actually being dragged — the native ghost image shows it
  // tracking the pointer; this marks the SOURCE node as picked up. Subtle
  // scale + shadow + fade, tokens only (mirrors AdminModuleNavbarRow's drag
  // lift in pages/admin/module.js).
  transform: (el, s) => (s.root?._navbarDragPath === s.path ? 'scale(1.04)' : 'scale(1)'),
  boxShadow: (el, s) => (s.root?._navbarDragPath === s.path ? 'shadow.5 0 W Y' : 'none'),
  opacity: (el, s) => (s.root?._navbarDragPath === s.path ? '0.9' : '1'),
  // Icons OFF: yesterday's text tab, padding `Y A1`. Icons ON (the default, Nika 2026-10-23): the tab IS the 24 px
  // glyph plate (`glyphPlateXs`) with `X` all round, so the round plate sits evenly in a round tab; the plate's own
  // states (GlyphPlateHolder) say hover, focus and current, so the tab paints no wash of its own.
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'Y',
  padding: 'Y A1',
  '&.withGlyph': {
    width: 'controlHCompact',
    height: 'controlHCompact',
    padding: '0',
    justifyContent: 'center',
    '@coarse': { width: 'controlH', height: 'controlH' },
    ':hover': { background: 'transparent' }
  },
  round: 'radiusPill',
  // Active wash — DECLARATIVE, so it re-runs whenever DOMQL re-applies props
  // (a collection state swap included, which is the one case no lifecycle hook
  // covers — WS-NAV-ACTIVE-TAB-DOES-NOT-TRACK-ROUTE-1). `selected` is the
  // design-system token the sidebar active item and the org-switcher rows use,
  // so every active nav surface reads identically (2026-06-18 per Nika "navbar
  // active item bg color should be same as sidebar active item bg color").
  // TOKENS, not a raw `var(--color-selected)`: a token emits an atomic class,
  // a raw value emits an inline style — and an inline base background outranks
  // the ':hover' wash below (the gotcha MobileTab's own factory documents).
  // An icon tab leaves "current" to its plate (inverted, with the dot).
  background: (el, s) => {
    const washed = !(el.call('homeLookRead', s && s.root) || {}).navbarIcons
    return el.scope.tabActive(s, el) && washed ? 'selected' : 'transparent'
  },
  border: 'none',
  // grab while reorder mode is armed, grabbing on the one tab actually being
  // dragged, plain pointer otherwise.
  cursor: (el, s) => {
    if (!s.root?._navbarReorder) return 'pointer'
    return s.root._navbarDragPath === s.path ? 'grabbing' : 'grab'
  },
  color: (el, s) => (el.scope.tabActive(s, el) ? 'title' : 'caption'),
  // Tabs render at NATURAL width — no shrink, no per-item "…" (2026-08-07
  // per Nika; supersedes the 2026-08-01 readable-floor ellipsis recipe).
  // When the strip's clamp binds, the Tabs CONTAINER scrolls horizontally
  // with a fade mask on the cut-off edge — a full label always reads fully,
  // excess tabs fade out at the strip edge instead of every tab degrading.
  flexShrink: '0',
  // Matched to the org switcher (item 4 per Nika). 'A' renders per
  // font-context (~15px here, ~12.5px in HeaderRight), so pin a scope-
  // independent rem on both so the top-navbar text is one size.
  fontSize: '0.9rem',
  '@tabletM': {
    fontSize: '0.78rem',
    padding: 'Y Z2'
  },
  // Keep all configured labels readable while the centered island is clamped
  // on tablet. Mobile hides the text-tab collection entirely.
  '@tabletS': {
    // The tablet lane is bounded by two live-measured islands. Use a denser
    // label recipe throughout that lane so names stay readable instead of
    // falling into ellipsis near its narrow end.
    fontSize: '0.72rem',
    padding: 'Y Z'
  },
  fontWeight: '700',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  transition:
    '140ms defaultBezier background, 140ms defaultBezier color, A defaultBezier transform, A defaultBezier box-shadow, A defaultBezier opacity',
  ':hover': { background: 'hover', color: 'title' },
  // N1 NAVIGATION (dropdowns spec 2026-09-30 §12): the tab's line glyph is the shared `NavTabGlyph` (24 px,
  // round, ringless in the dense bar); the one state logic comes from `GlyphPlateHolder` in this tab's extends
  // (shared/components/AppGlyphPlate.js). OPTIONAL, default OFF (Nika 2026-10-02: "on navbar make icons optional
  // from personalize default to off"): the person's look `navbarIcons` (workspace functions/homeLook.js) shows
  // it; off = display none, so no gap is left behind.
  Glyph: {
    extends: 'NavTabGlyph',
    // `hide` (display: none), reactive to the switch; a hidden flex item takes no gap. A navbar RAIL
    // (functions/navbarLayout.js) always shows the glyph: a rail is as narrow as one plate.
    hide: (el, s) =>
      !(
        (el.call('homeLookRead', s && s.root) || {}).navbarIcons ||
        (el.call('navbarLayoutRead', s && s.root) || {}).rail
      )
  },
  class: {
    withGlyph: (el, s) =>
      !!(
        (el.call('homeLookRead', s && s.root) || {}).navbarIcons ||
        (el.call('navbarLayoutRead', s && s.root) || {}).rail
      )
  },
  // A WIDE RAIL (Nika 2026-10-24: "left and right sidebars can be toggle expandable") names each tab beside
  // its glyph — a row the rail's width (HeaderTabs.Tabs), the name in the label weight.
  NavLabel: {
    tag: 'span',
    hide: (el, s) => !(el.call('navbarLayoutRead', s && s.root) || {}).expanded,
    text: (el, s) => el.call('storeItemText', { key: s.appKey, name: s.label }, 'name'),
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: '500'
  },
  // A rail's tab always wears the glyph (`withGlyph` above), and `&.withGlyph`
  // writes `padding` at the attribute rule's own specificity: the wide row
  // compounds both, so it wins whatever order the sheet holds them in.
  'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s && s.root) || {}).railMode,
  // The plate keeps its hover/current treatment, while its 16px glyph and
  // following label share the same columns as the rail's plain action icons.
  '&[data-nav-rail="wide"] > [data-key="Glyph"]': {
    marginInline: 'calc((var(--size-iconSm) - var(--size-glyphPlateXs)) / 2)'
  },
  '&.withGlyph[data-nav-rail="wide"]': {
    width: '100%',
    justifyContent: 'flex-start',
    gap: 'Z',
    padding: 'Y Z',
    minHeight: 'controlHCompact',
    round: 'radiusControl',
    boxSizing: 'border-box',
    '@coarse': { minHeight: 'controlH' }
  },

  // ONE NAME FOR ONE APP. Three kinds of row reach this single call, and the
  // ORDER inside `storeItemText` (shared/functions/storeItemText.js) is what
  // lets them share it without a branch here:
  //   • an INSTALLED APP's tab carries `appKey`, the app's own store key
  //     (workspaceConfigs.js `_appRowsWithKey`), so the label resolves to the
  //     name the App Store shows for that same record — this tab used to print
  //     the app manifest's English literal instead, so a Georgian workspace
  //     read "Warehouse" where the store said "ინვენტარი";
  //   • a SHIPPED house tab (`shell.app.studio`) has no `appKey`, so the
  //     resolver falls to its last source, which is `polyglot` — byte-identical
  //     to the plain `el.call('polyglot', s.label)` this replaced;
  //   • a TENANT's own label ("Ops", a Georgian phrase) resolves nothing at any
  //     source and comes back verbatim, because `translate()` returns its input
  //     for a key nobody ships. That property is what makes key-in-data safe
  //     and is pinned in workspace/__tests__/navbarLabelI18n.test.js.
  //
  // ICONS (Nika 2026-10-23: "try changing text navbar items with icons, with beautiful floating tooltip on hover"):
  // the same name, off the tab — the house tooltip shows it on hover and keyboard focus (TitleTooltipTrigger: the
  // shell's one floating TooltipRoot, never the native title) and it is the tab's accessible name. A text tab
  // carries its name on its face, so it wants no tooltip.
  // A RAIL's tab is a glyph too (Glyph above): its name goes to the tooltip — or, on a wide rail, to its
  // face (NavLabel), so no tooltip there.
  text: (el, s) =>
    (el.call('homeLookRead', s && s.root) || {}).navbarIcons ||
    (el.call('navbarLayoutRead', s && s.root) || {}).rail
      ? ''
      : el.call('storeItemText', { key: s.appKey, name: s.label }, 'name'),
  title: (el, s) => {
    const nav = el.call('navbarLayoutRead', s && s.root) || {}
    return ((el.call('homeLookRead', s && s.root) || {}).navbarIcons || nav.rail) && !nav.expanded
      ? el.call('storeItemText', { key: s.appKey, name: s.label }, 'name')
      : null
  },
  'aria-label': (el, s) =>
    (el.call('homeLookRead', s && s.root) || {}).navbarIcons ||
    (el.call('navbarLayoutRead', s && s.root) || {}).rail
      ? el.call('storeItemText', { key: s.appKey, name: s.label }, 'name')
      : null,

  // Every navbar tab — built-in app-window routes (Studio/Tasks/Files/Team) AND
  // installed workspace-module tabs (e.g. Contacts '/crm', CMS '/cms', GreenIG
  // Claims, AI House Users/Listings) — opens its NON-CANVAS route INSIDE the
  // shell PageSlot (root._canvasSlot), never a hard top-level navigation; canvas
  // tabs stay full-page (openAppPage's isCanvasRoute wins). cmd/ctrl+click opens
  // the tab in the SECOND pane (iPad split view).
  //
  // Pointer choreography (see the long-press block above _openTab): pointerdown
  // arms the 450ms reorder long-press with pointer CAPTURE — the capture keeps
  // the up event on this node across reactive re-renders, the same dead-click
  // window the old navigate-on-mousedown dodged — a short press navigates on
  // pointerup, a hold enters reorder mode instead. onClick covers KEYBOARD
  // activation only (detail === 0; a pointer click already navigated on up).
  onPointerdown: (e, el, s) => el.scope.lpStart(e, el, s),
  onPointermove: (e, el) => el.scope.lpMove(e, el),
  onPointerup: (e, el, s) => el.scope.lpEnd(e, el, s),
  onPointercancel: (e, el) => el.scope.lpCancel(el),
  onClick: (e, el, s) => {
    if (e.detail === 0) el.scope.openTab(e, el, s)
  },

  // Reorder-mode drag-and-drop (`draggable` flips on with the mode above).
  onDragstart: (e, el, s) => {
    if (!s.root?._navbarReorder || !s.path) {
      e.preventDefault()
      return
    }
    e.dataTransfer.setData('text/nav-tab', s.path)
    e.dataTransfer.effectAllowed = 'move'
    s.root.update(
      { _navbarDragPath: s.path, _navbarDragOverPath: null, _navbarDragOverAfter: false },
      { preventFetch: true }
    )
  },
  onDragover: (e, el, s) => el.scope.dragOver(e, el, s),
  onDrop: (e, el, s) => el.scope.dropTab(e, el, s),
  // Native drag ALWAYS fires dragend on the source — dropped on a valid
  // target, dropped outside one, or cancelled (Escape mid-drag) — so this is
  // the one handler guaranteed to run and clear the lift/indicator even when
  // no `drop` event fires at all (a cancelled drag used to leave the tab
  // permanently lifted).
  onDragend: (e, el, s) => {
    if (!s.root?._navbarDragPath && !s.root?._navbarDragOverPath) return
    s.root.update(
      { _navbarDragPath: null, _navbarDragOverPath: null, _navbarDragOverAfter: false },
      { preventFetch: true }
    )
  },

  // Right-click → the house ContextDropdown with this tab's actions (Pin /
  // Open in side view / Remove — 2026-07-27 per Nika). Rows + dispatch live
  // in shared/functions/navbarTabMenu.js; `navTab` rides the dropdown state
  // so the registered itemsFn/actionFn know which tab was targeted.
  // stopPropagation keeps the pill-background reset menu (HeaderTabs root)
  // from double-firing.
  onContextmenu: (e, el, s) => {
    if (s.placeholder || !s.path) return
    e.preventDefault()
    e.stopPropagation()
    s.root.update(
      {
        contextDropdown: {
          open: true,
          x: e.clientX,
          y: e.clientY,
          itemsFn: 'navbarTabMenuItems',
          actionFn: 'navbarTabMenuAction',
          navTab: { key: s.key, label: s.label, path: s.path }
        }
      },
      { preventFetch: true }
    )
  },

  // Insertion indicator — a thin accent bar rendered on whichever tab is the
  // CURRENT native dragover target, before/after per _dragAfter (the exact
  // geometry _dropTab lands with, so the bar never points at a spot the drop
  // won't honor). pointerEvents: 'none' so it can never intercept the very
  // dragover it's reacting to — events keep hitting this button underneath,
  // uninterrupted.
  InsertBar: {
    tag: 'span',
    'aria-hidden': 'true',
    position: 'absolute',
    top: 'W',
    bottom: 'W',
    width: 'hairline2',
    round: 'V',
    background: 'highlight',
    pointerEvents: 'none',
    zIndex: '1',
    hide: (el, s) =>
      !s.root?._navbarReorder ||
      !s.root._navbarDragOverPath ||
      s.root._navbarDragOverPath !== s.path ||
      s.path === s.root._navbarDragPath,
    left: (el, s) => (s.root?._navbarDragOverAfter ? 'auto' : '-W'),
    right: (el, s) => (s.root?._navbarDragOverAfter ? '-W' : 'auto')
  }
}

// ── Mobile LABEL strip — one tab (MobileTabs, above) ────────────────────────

// One label in the mobile nav strip. `state` is a HEADER_TABS-shaped entry
// ({ key, label, path }) — same shape HeaderTab renders, text instead of an
// icon (see the MobileTabs comment above for why: config-driven navbar rows
// carry no icon metadata, so an icon-strip degenerates to identical glyphs
// for every module tab on a real workspace). `text:` directly (matches
// HeaderTab's own approach) rather than a nested Label child — there is no
// icon here to coexist with, so the textContent-vs-children conflict that
// justified a separate component from HeaderTab in the first place doesn't
// apply to what this button renders; it's still its OWN component because
// its SIZING (auto-width pill, scrollable, vs HeaderTab's ellipsizing-
// within-a-clamped-pill) is genuinely different from HeaderTab's — the
// ACTIVE-STATE treatment is intentionally the SAME (see the background/color
// factories below).
export const MobileTab = {
  // The house tooltip, fed by `title` (TitleTooltipTrigger keeps the native one off).
  extends: ['AppControl', 'NavbarTooltipTrigger', 'GlyphPlateHolder'],
  compact: true,
  scope: {
    matchesPath(tabPath, p) {
      if (!tabPath) return false
      if (tabPath === '/') return p === '/'
      const tab = canonicalRoutePath(tabPath)
      const path = canonicalRoutePath(p)
      return path === tab || path.startsWith(tab + '/')
    },
    navMatchPath(root) {
      if (typeof window === 'undefined') return '/'
      const slot = root && root._canvasSlot
      if (slot) return String(slot).split('?')[0]
      let p = stripWorkspacePrefix(window.location.pathname)
      try {
        const page = new URLSearchParams(window.location.search).get('page')
        // The param carries the FULL slot route, query included, since a
        // module record deep link puts its own params in it (A1-06). The tab
        // match is a PATH comparison, so strip them here exactly as the slot
        // branch above already does — otherwise a shared room link matches no
        // tab at all and the strip goes dark.
        if (page) p = String(page).split('?')[0].split('#')[0]
      } catch (_) {}
      return p
    },
    // Route-match only, no styling opinion — shared by MobileTab's
    // declarative reactive props and its imperative sync below, so the two
    // can never disagree about what counts as "active".
    mobileTabActive(path, root) {
      return this.matchesPath(path, this.navMatchPath(root))
    },
    // State-derived form — the one the imperative sync below uses, so a
    // collection state swap on a reused node can never leave it matching the
    // PREVIOUS tab's route (WS-NAV-ACTIVE-TAB-DOES-NOT-TRACK-ROUTE-1).
    // The LONGEST matching tab of the strip wins — the same rule as
    // HeaderTab.tabActive (NAVBAR-ACTIVE-LONGEST-MATCH-1), over the strip's
    // own list (the row's visibleNavTabs).
    tabActive(s, el) {
      const p = this.navMatchPath(s && s.root)
      if (!this.mobileTabActive(s && s.path, s && s.root)) return false
      const row = el && el.parent
      const list =
        row && row.scope && typeof row.scope.visibleNavTabs === 'function'
          ? row.scope.visibleNavTabs(s.root, row)
          : null
      if (!Array.isArray(list)) return true
      const mine = canonicalRoutePath(s.path)
      return !list.some((t) => {
        const other = t && t.path ? canonicalRoutePath(t.path) : ''
        return other.length > mine.length && this.matchesPath(t.path, p)
      })
    },
    // Open a navbar tab's route — duplicated verbatim from HeaderTab's own
    // scope.
    openTab(e, el, s) {
      if (s.placeholder || !s.path) return
      if (s.root?._navbarReorder) return
      e.preventDefault()
      if (s.path === '/') {
        el.router('/', el.getRoot())
        return
      }
      try {
        el.call('openAppPage', s.path, { preferSlot: true, split: e.metaKey || e.ctrlKey })
        return
      } catch (_) {}
      el.router(s.path, el.getRoot())
    }
  },
  tag: 'button',
  type: 'button',
  flexShrink: '0',
  position: 'relative',
  scrollSnapAlign: 'center',
  // Give the text pill a physical touch row inside its scroll strip.
  '@coarse': {
    position: 'relative',
    ...touchRow
  },
  height: 'B1',
  round: 'radiusPill',
  border: 'none',
  cursor: 'pointer',
  // Tight but legible — a denser recipe than HeaderTab's own base (this
  // strip's whole purpose is fitting more than one destination in view at
  // once).
  //
  // TOKENS, not rem (2026-09-04 per Nika "on mobile version, navbars and text
  // are too small"). This was `fontSize: '0.72rem'`, matched to HeaderTab's
  // '@tabletS' step for cross-lane consistency — but "scope-independent" is
  // only half true: rem is independent of the ELEMENT's font context and
  // still rides the ROOT, and this shell's root is itself responsive. It
  // computes 13px in a 390px viewport (16px at 1196px), so this strip painted
  // at 9.36px in a 19px-tall row — the smallest text in the product, on the
  // one surface that is pure navigation. `Z` holds 12.04px there, and
  // `controlHCompact` (36px, designSystem/sizes.js) gives the row a real
  // touch height instead of 19px; both are absolute where it matters and
  // neither tracks a root that shrinks on phones.
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'Y',
  // icons OFF (the default): yesterday's 'V Z'; icons ON: the plate leads close to the start
  padding: 'V Z',
  '&.withGlyph': { padding: 'V Z V W' },
  fontSize: 'Z',
  minHeight: 'controlHCompact',
  fontWeight: '700',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  attr: { 'data-tab-path': (el, s) => s.path },
  // Same one resolver as HeaderTab.text above (see its note for why all three
  // kinds of row share it) — the FUNCTION form, as every HTML-attribute prop
  // must take (docs/I18N-CONVENTION.md §1).
  'aria-label': (el, s) => el.call('storeItemText', { key: s.appKey, name: s.label }, 'name'),
  title: (el, s) => el.call('storeItemText', { key: s.appKey, name: s.label }, 'name'),
  // Primary navigation landmark's children expose the CURRENT destination —
  // aria-current is what tells assistive tech which one that is.
  'aria-current': (el, s) => (el.scope.tabActive(s, el) ? 'page' : 'false'),
  // `_navActivePath` — the route HeaderTabs.onCreate publishes on
  // popstate/locationchange; the trigger that re-runs these factories on a
  // navigation that touches no other subscribed key.
  subscribeTo: ['_canvasSlot', '_navActivePath'],
  // The same reveal as HeaderTab: the update in which this tab becomes the
  // current destination brings it into full view.
  onCreate: (el) => {
    el._wasActive = el.scope.tabActive(el.state, el)
  },
  onUpdate: (el) => {
    const active = el.scope.tabActive(el.state, el)
    if (active && !el._wasActive) el.parent?.scope?.edgeFadeBecameCurrent?.(el.parent, el.node)
    el._wasActive = active
  },
  // Declarative, and the ONLY writer of the wash (background/color are
  // CSS_PROPS_REGISTRY properties, so a direct factory — not a `.isX` class —
  // re-runs on a subscribed change AND on the prop re-apply a collection
  // re-render performs). The imperative create/update self-heal that used to
  // sit beside this is gone: no lifecycle hook fires when a reused child node
  // takes a DIFFERENT tab's state, so the inline styles it wrote survived as
  // the WRONG tab's highlight (WS-NAV-ACTIVE-TAB-DOES-NOT-TRACK-ROUTE-1).
  // TOKEN values, not `var(--color-selected)`: a token emits an atomic class
  // that re-emits on every prop pass, where a raw value emits an inline style
  // that outranks the ':hover' class wash below.
  background: (el, s) => (el.scope.tabActive(s, el) ? 'selected' : 'transparent'),
  color: (el, s) => (el.scope.tabActive(s, el) ? 'title' : 'caption'),
  transition: '140ms defaultBezier background, 140ms defaultBezier color',
  ':hover': { background: 'hover', color: 'title' },
  // N1 NAVIGATION (dropdowns spec 2026-09-30 §12): the tab's line glyph is the shared `NavTabGlyph` (24 px,
  // round, ringless in the dense bar); the one state logic comes from `GlyphPlateHolder` in this tab's extends
  // (shared/components/AppGlyphPlate.js). OPTIONAL, default OFF (Nika 2026-10-02: "on navbar make icons optional
  // from personalize default to off"): the person's look `navbarIcons` (workspace functions/homeLook.js) shows
  // it; off = display none, so no gap is left behind.
  Glyph: {
    extends: 'NavTabGlyph',
    // `hide` (display: none), reactive to the switch; a hidden flex item takes no gap
    hide: (el, s) => !(el.call('homeLookRead', s && s.root) || {}).navbarIcons
  },
  class: { withGlyph: (el, s) => !!(el.call('homeLookRead', s && s.root) || {}).navbarIcons },
  // pointerup (not mousedown/click alone) — survives a reactive re-render
  // swap the way HeaderTab's long-press-aware handlers do; no reorder mode
  // to arbitrate against here, so this is the only handler this needs.
  // onClick's detail===0 gate covers keyboard activation only (a pointer tap
  // already navigated on pointerup).
  onPointerup: (e, el, s) => {
    if (e.button === 0) el.scope.openTab(e, el, s)
  },
  onClick: (e, el, s) => {
    if (e.detail === 0) el.scope.openTab(e, el, s)
  },

  // The mobile strip renders the SAME rows as HeaderTab, so it resolves them
  // the same way — a tab that reads "ინვენტარი" on desktop and "Warehouse" on
  // a phone is the same drift with a smaller viewport.
  text: (el, s) => el.call('storeItemText', { key: s.appKey, name: s.label }, 'name')
}
